/**
 * A Team system's needs on Waiting on you: what Explore found that only a
 * person can answer (a credential, test data, access, or a capability YAGNI
 * does not have yet), one group per Team.
 *
 * The asks come from `GET /api/system-environments/asks`, which answers 404
 * when the workspace does not run on Machines; then this renders nothing.
 * Any other failed read says so, with a retry, rather than looking empty.
 * A workspace admin answers here: types a value (sent once, never shown
 * again and never echoed back), marks one provided, or declines it. A Team
 * owner who is not an admin sees the asks and their status only.
 */
import type { SystemNeedAsk } from "@yagni/shared";
import { useCallback, useEffect, useState } from "react";

import { deskAccentToken } from "../desks/deskAccent.js";
import { useCan } from "../hooks/useWorkspaceRole.js";
import { readJsonError } from "../lib/readJsonError.js";
import { Group } from "../queue/index.js";
import { captureUiError } from "../sentry.js";
import { Button, Input } from "../ui/index.js";

/**
 * `system`: a need of the workspace System, listed once however many Teams
 * share the part that needs it; `teamId` is the Team it is answered through.
 */
type TeamAsk = SystemNeedAsk & { teamId: string; system?: boolean };

/** The System's asks come first, under one group of their own. */
const SYSTEM_GROUP = "system";

export function useSystemNeedAsks(): {
  asks: TeamAsk[];
  failed: boolean;
  reload: () => Promise<void>;
} {
  const [asks, setAsks] = useState<TeamAsk[]>([]);
  const [failed, setFailed] = useState(false);
  const reload = useCallback(async () => {
    try {
      const res = await fetch("/api/system-environments/asks", { credentials: "include" });
      // 404 is the flag being off: nothing to show, nothing wrong.
      if (res.status === 404) {
        setAsks([]);
        setFailed(false);
        return;
      }
      if (!res.ok) throw new Error(`system needs read answered ${res.status}`);
      const body = (await res.json()) as { asks?: TeamAsk[] };
      setAsks(Array.isArray(body.asks) ? body.asks : []);
      setFailed(false);
    } catch (error) {
      captureUiError(error, { area: "Work", operation: "read_system_need_asks" });
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { asks, failed, reload };
}

function NeedAsk({
  ask,
  canAnswer,
  onAnswered,
}: {
  ask: TeamAsk;
  canAnswer: boolean;
  onAnswered: () => Promise<void>;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (action: "provide" | "decline", body?: Record<string, string>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/system-environments/teams/${ask.teamId}/needs/${ask.needId}/${action}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body ?? {}),
        },
      );
      if (!res.ok) {
        setError(await readJsonError(res));
        return;
      }
      setValue("");
      await onAnswered();
    } catch {
      setError("That did not go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row need" data-testid={`system-need-${ask.needId}`}>
      <div>
        <strong>{ask.title}</strong>
        <p>{ask.body}</p>
        {canAnswer && (
          <form
            className="flex flex-wrap items-center gap-xs"
            onSubmit={(event) => {
              event.preventDefault();
              if (ask.answer === "enter_value" && value) void send("provide", { secret: value });
            }}
          >
            {ask.answer === "enter_value" && (
              <>
                <Input
                  type="password"
                  autoComplete="off"
                  inputSize="sm"
                  aria-label={`Value for ${ask.name}`}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                />
                <Button type="submit" size="sm" disabled={!value} loading={busy}>
                  Save
                </Button>
              </>
            )}
            {ask.answer === "mark_provided" && (
              <Button type="button" size="sm" loading={busy} onClick={() => void send("provide")}>
                Mark provided
              </Button>
            )}
            {ask.declinable && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void send("decline")}
              >
                Decline
              </Button>
            )}
          </form>
        )}
        {error && <p role="alert">{error}</p>}
      </div>
    </div>
  );
}

interface SystemNeedAsksProps {
  asks: TeamAsk[];
  failed?: boolean;
  reload: () => Promise<void>;
  teamName: (teamId: string) => string;
}

/** One group per Team with open system needs, after the ticket groups. */
export function SystemNeedAsks(props: SystemNeedAsksProps) {
  if (props.failed) {
    return (
      <div role="alert" data-testid="system-need-asks-failed">
        <p>Your systems' needs could not be loaded.</p>
        <Button type="button" size="sm" variant="ghost" onClick={() => void props.reload()}>
          Try again
        </Button>
      </div>
    );
  }
  if (props.asks.length === 0) return null;
  return <SystemNeedGroups {...props} />;
}

function SystemNeedGroups({ asks, reload, teamName }: SystemNeedAsksProps) {
  const canAnswer = useCan("manage-repos");
  const groupOf = (ask: TeamAsk) => (ask.system ? SYSTEM_GROUP : ask.teamId);
  const groups = [...new Set(asks.map(groupOf))].sort(
    (a, b) => Number(b === SYSTEM_GROUP) - Number(a === SYSTEM_GROUP),
  );
  return (
    <div data-testid="system-need-asks">
      {groups.map((group) => (
        <Group
          key={group}
          label={
            group === SYSTEM_GROUP ? "Your system needs" : `${teamName(group)}: its system needs`
          }
          tone="needs"
          dot={group === SYSTEM_GROUP ? undefined : deskAccentToken(group)}
          plain
        >
          {asks
            .filter((ask) => groupOf(ask) === group)
            .map((ask) => (
              <NeedAsk key={ask.needId} ask={ask} canAnswer={canAnswer} onAnswered={reload} />
            ))}
        </Group>
      ))}
    </div>
  );
}
